import { useBrowserStorage } from '#hooks/useBrowserStorage.ts';
import { useCookie } from '#hooks/useCookie.ts';
import { AUTH_TOKEN_NAME, OIDC_TOKEN_NAME, USER_TOKEN_NAME } from '#util/cookies.ts';

export function useAuthState() {
  const [oidcStateCode, setOIDCStateCode, clearOIDCStateCode] = useBrowserStorage<string>('oidc-state');
  // Once a user visits the oidc login url, we save it in localStorage to allow automatic redirects when tokens
  // expire and a button on the login page for SSO.  It needs to be cleared if the server returns a 404 when starting oidc,
  // which means the identity provider was deleted.
  const [oidcLoginURL, setOIDCLoginURL, clearOIDCLoginURL] = useBrowserStorage<string>('loginURL', 'local');
  // Should we automatically redirect to the oidc login url?
  // We track this separately from the login url itself so that we can allow logout without clearing the login url.
  const [redirectToOIDC, setRedirectToOIDC, clearRedirectToOIDC] = useBrowserStorage<string>('redirectToOIDC', 'local');
  // When performing OIDC re-auth, we want to return the user to the page they were on in our app
  const [oidcFrom, setOIDCFrom, clearOIDCFrom] = useBrowserStorage<string>('oidc-from');

  // The userToken corresponds to the sessionToken from the server.  It requires a magic link to obtain.
  const [userToken, setUserToken, deleteUserToken, getCurrentUserToken] = useCookie(USER_TOKEN_NAME);
  // The authToken is valid for a short period of time (16 hours) and requires 2FA to obtain.
  const [authToken, setAuthToken, deleteAuthToken] = useCookie(AUTH_TOKEN_NAME);
  // The oidcToken is an auth token that was obtained from an OIDC authentication
  const [oidcToken, setOIDCToken, deleteOIDCToken] = useCookie(OIDC_TOKEN_NAME);

  return {
    oidcState: { code: oidcStateCode, set: setOIDCStateCode, clear: clearOIDCStateCode },
    oidcLogin: { url: oidcLoginURL, set: setOIDCLoginURL, clear: clearOIDCLoginURL },
    oidcRedirect: { shouldRedirect: Boolean(redirectToOIDC), set: setRedirectToOIDC, clear: clearRedirectToOIDC },
    oidcFrom: { url: oidcFrom, set: setOIDCFrom, clear: clearOIDCFrom },
    userToken: { value: userToken, set: setUserToken, clear: deleteUserToken, getCurrent: getCurrentUserToken },
    authToken: { value: authToken, set: setAuthToken, clear: deleteAuthToken },
    oidcToken: { value: oidcToken, set: setOIDCToken, clear: deleteOIDCToken },
  };
}
